import type { KdsFlowDetail, KdsTaskDetail, KdsTaskDetailStatus } from '@ordr-as/ordr-service-types/epos';
import type { ChipState, ExpoDisplayItem, ExpoOrder, FlowGraph, ItemTotal } from './types';

export const RECENTLY_DONE_CAP = 20;

function computeDepths(flowStationIds: number[], upstreamsByFlowStationId: Map<number, number[]>): Map<number, number> {
  // Longest path from a flow's start; flows are tiny, so relax until stable.
  const depth = new Map<number, number>(flowStationIds.map((id) => [id, 0]));
  for (let pass = 0; pass < flowStationIds.length; pass++) {
    let changed = false;
    for (const id of flowStationIds) {
      for (const upstream of upstreamsByFlowStationId.get(id) ?? []) {
        const candidate = (depth.get(upstream) ?? 0) + 1;
        if (candidate > (depth.get(id) ?? 0)) {
          depth.set(id, candidate);
          changed = true;
        }
      }
    }
    if (!changed) break;
  }
  return depth;
}

export function buildFlowGraph(flowDetails: KdsFlowDetail[]): FlowGraph {
  const stationIdByFlowStationId = new Map<number, number>();
  const upstreamsByFlowStationId = new Map<number, number[]>();
  const flowStationIdsByStationId = new Map<number, number[]>();

  for (const flow of flowDetails) {
    for (const flowStation of flow.stations ?? []) {
      if (flowStation.id === undefined || flowStation.stationId === undefined) continue;
      stationIdByFlowStationId.set(flowStation.id, flowStation.stationId);
      const flowStationIds = flowStationIdsByStationId.get(flowStation.stationId) ?? [];
      flowStationIds.push(flowStation.id);
      flowStationIdsByStationId.set(flowStation.stationId, flowStationIds);
    }
    for (const link of flow.links ?? []) {
      if (link.upstreamFlowStationId === undefined || link.downstreamFlowStationId === undefined) continue;
      const upstreams = upstreamsByFlowStationId.get(link.downstreamFlowStationId) ?? [];
      upstreams.push(link.upstreamFlowStationId);
      upstreamsByFlowStationId.set(link.downstreamFlowStationId, upstreams);
    }
  }

  const depthByFlowStationId = computeDepths([...stationIdByFlowStationId.keys()], upstreamsByFlowStationId);

  return { stationIdByFlowStationId, upstreamsByFlowStationId, flowStationIdsByStationId, depthByFlowStationId };
}

export function taskStationId(task: KdsTaskDetail, graph: FlowGraph): number | undefined {
  if (task.flowStationId === undefined) return undefined;
  return graph.stationIdByFlowStationId.get(task.flowStationId);
}

/** Statuses that keep a task on the board. RECALLED came back from done and needs attention again. */
export function isActiveStatus(status: KdsTaskDetailStatus | undefined): boolean {
  return status === 'PENDING' || status === 'QUEUED' || status === 'ONGOING' || status === 'RECALLED';
}

/** RECALLED renders like ONGOING: it is back in the kitchen's hands. */
export function isOngoingStatus(status: KdsTaskDetailStatus | undefined): boolean {
  return status === 'ONGOING' || status === 'RECALLED';
}

function isUnfinishedStatus(status: KdsTaskDetailStatus | undefined): boolean {
  return status !== 'DONE' && status !== 'VOIDED';
}

export function selectStationTasks(tasks: KdsTaskDetail[], stationId: number, graph: FlowGraph): KdsTaskDetail[] {
  return tasks.filter((task) => isActiveStatus(task.status) && taskStationId(task, graph) === stationId);
}

function boardRank(status: KdsTaskDetailStatus | undefined): number {
  if (isOngoingStatus(status)) return 0;
  if (status === 'QUEUED') return 1;
  return 2;
}

function createdAtMs(task: KdsTaskDetail): number {
  // Tasks without a timestamp sort last within their rank.
  return task.createdUtc ? new Date(task.createdUtc).getTime() : Number.MAX_SAFE_INTEGER;
}

export function sortBoardTasks(tasks: KdsTaskDetail[]): KdsTaskDetail[] {
  return [...tasks].sort((a, b) => boardRank(a.status) - boardRank(b.status) || createdAtMs(a) - createdAtMs(b));
}

/**
 * The station a PENDING task is waiting on: walk the flow links upstream and
 * return the station of the earliest (deepest upstream) sibling task — same
 * groupId — that is not finished yet.
 */
export function resolveWaitingFor(
  task: KdsTaskDetail,
  allTasks: KdsTaskDetail[],
  graph: FlowGraph
): number | undefined {
  if (task.flowStationId === undefined) return undefined;

  const unfinishedSiblingFlowStations = new Set<number>();
  for (const sibling of allTasks) {
    if (sibling.groupId !== task.groupId || sibling.id === task.id) continue;
    if (sibling.flowStationId === undefined || !isUnfinishedStatus(sibling.status)) continue;
    unfinishedSiblingFlowStations.add(sibling.flowStationId);
  }
  if (unfinishedSiblingFlowStations.size === 0) return undefined;

  // BFS upstream; the deepest unfinished flow station is the earliest in the route.
  let blockingFlowStationId: number | undefined;
  const visited = new Set<number>([task.flowStationId]);
  let frontier = [task.flowStationId];
  while (frontier.length > 0) {
    const next: number[] = [];
    for (const flowStationId of frontier) {
      for (const upstream of graph.upstreamsByFlowStationId.get(flowStationId) ?? []) {
        if (visited.has(upstream)) continue;
        visited.add(upstream);
        next.push(upstream);
        if (unfinishedSiblingFlowStations.has(upstream)) blockingFlowStationId = upstream;
      }
    }
    frontier = next;
  }

  if (blockingFlowStationId === undefined) return undefined;
  return graph.stationIdByFlowStationId.get(blockingFlowStationId);
}

export function selectStationCounts(tasks: KdsTaskDetail[], graph: FlowGraph): Map<number, number> {
  const counts = new Map<number, number>();
  for (const task of tasks) {
    if (!isActiveStatus(task.status)) continue;
    const stationId = taskStationId(task, graph);
    if (stationId === undefined) continue;
    counts.set(stationId, (counts.get(stationId) ?? 0) + 1);
  }
  return counts;
}

function updatedAtMs(task: KdsTaskDetail): number {
  const stamp = task.updatedUtc ?? task.createdUtc;
  return stamp ? new Date(stamp).getTime() : 0;
}

function minCreatedUtc(tasks: KdsTaskDetail[]): string | undefined {
  const stamps = tasks.map((t) => t.createdUtc).filter((s): s is string => Boolean(s));
  return stamps.sort()[0];
}

function maxUpdatedUtc(tasks: KdsTaskDetail[]): string | undefined {
  const stamps = tasks.map((t) => t.updatedUtc ?? t.createdUtc).filter((s): s is string => Boolean(s));
  return stamps.sort().at(-1);
}

function toExpoOrder(groupId: string, tasks: KdsTaskDetail[]): ExpoOrder {
  const doneCount = tasks.filter((t) => t.status === 'DONE').length;
  return {
    groupId,
    bookingOrderId: tasks.find((t) => t.bookingOrderId !== undefined)?.bookingOrderId,
    createdUtc: minCreatedUtc(tasks),
    updatedUtc: maxUpdatedUtc(tasks),
    eatIn: tasks.some((t) => t.eatIn === true),
    orderComment: tasks.find((t) => t.orderComment)?.orderComment,
    tasks,
    doneCount,
    totalCount: tasks.length,
    allDone: tasks.length > 0 && doneCount === tasks.length,
  };
}

function expoRank(order: ExpoOrder): number {
  if (order.allDone) return 0;
  const started = order.tasks.some((t) => isOngoingStatus(t.status) || t.status === 'DONE');
  return started ? 1 : 2;
}

/**
 * Groups tasks by order (groupId) for the Expo board. Ready (all done) orders
 * sort first, then in-progress, then not-yet-started; ready orders also feed
 * the "Recently served" list (newest first).
 */
export function selectExpoOrders(tasks: KdsTaskDetail[]): { board: ExpoOrder[]; recentlyServed: ExpoOrder[] } {
  const byGroup = new Map<string, KdsTaskDetail[]>();
  for (const task of tasks) {
    if (!task.groupId || task.status === 'VOIDED') continue;
    const group = byGroup.get(task.groupId) ?? [];
    group.push(task);
    byGroup.set(task.groupId, group);
  }

  const orders = [...byGroup.entries()].map(([groupId, groupTasks]) => toExpoOrder(groupId, groupTasks));

  const board = [...orders].sort(
    (a, b) => expoRank(a) - expoRank(b) || (a.createdUtc ?? '').localeCompare(b.createdUtc ?? '')
  );

  const recentlyServed = orders
    .filter((o) => o.allDone)
    .sort((a, b) => (b.updatedUtc ?? '').localeCompare(a.updatedUtc ?? ''))
    .slice(0, RECENTLY_DONE_CAP);

  return { board, recentlyServed };
}

function chipState(status: KdsTaskDetailStatus | undefined): ChipState {
  if (status === 'DONE') return 'done';
  if (isOngoingStatus(status)) return 'ongoing';
  return 'todo';
}

/**
 * Builds an Expo card's order lines: the same order line appears in one task
 * per station of its route, so group by orderLineId and turn each task into a
 * progress chip, ordered by the flow's station sequence.
 */
export function selectExpoDisplayItems(order: ExpoOrder, graph: FlowGraph): ExpoDisplayItem[] {
  const byLine = new Map<string, ExpoDisplayItem>();

  for (const task of order.tasks) {
    for (const item of task.items ?? []) {
      const key = item.orderLineId !== undefined ? String(item.orderLineId) : `${task.id}-${item.id}`;
      const line = byLine.get(key) ?? {
        key,
        name: item.itemName ?? 'Unknown item',
        quantity: item.quantity ?? 1,
        modifiers: [],
        comment: undefined,
        chips: [],
      };
      // The same line repeats in one task per station; merge details across copies.
      const modifiers = (item.modifiers ?? []).map((m) => m.itemName ?? '').filter(Boolean);
      line.modifiers = [...new Set([...line.modifiers, ...modifiers])];
      line.comment = line.comment ?? item.comment;
      line.chips.push({ taskId: task.id, stationId: taskStationId(task, graph), state: chipState(task.status) });
      byLine.set(key, line);
    }
  }

  const depthOf = (taskId: number | undefined, fallback: number) => {
    const task = order.tasks.find((t) => t.id === taskId);
    if (task?.flowStationId === undefined) return fallback;
    return graph.depthByFlowStationId.get(task.flowStationId) ?? fallback;
  };

  for (const line of byLine.values()) {
    line.chips.sort((a, b) => depthOf(a.taskId, 0) - depthOf(b.taskId, 0));
  }

  return [...byLine.values()];
}

export function selectItemTotals(tasks: KdsTaskDetail[]): ItemTotal[] {
  const totals = new Map<string, number>();
  for (const task of tasks) {
    if (!isActiveStatus(task.status)) continue;
    for (const item of task.items ?? []) {
      const name = item.itemName ?? 'Unknown item';
      totals.set(name, (totals.get(name) ?? 0) + (item.quantity ?? 1));
    }
  }
  return [...totals.entries()]
    .map(([name, quantity]) => ({ name, quantity }))
    .sort((a, b) => b.quantity - a.quantity || a.name.localeCompare(b.name));
}

export function selectRecentlyDone(tasks: KdsTaskDetail[], stationId: number, graph: FlowGraph): KdsTaskDetail[] {
  return tasks
    .filter((task) => task.status === 'DONE' && taskStationId(task, graph) === stationId)
    .sort((a, b) => updatedAtMs(b) - updatedAtMs(a))
    .slice(0, RECENTLY_DONE_CAP);
}

export function taskSummaryLine(task: KdsTaskDetail): string {
  return (task.items ?? [])
    .map((item) => {
      const qty = item.quantity ?? 1;
      const name = item.itemName ?? 'Unknown item';
      return qty > 1 ? `${qty}× ${name}` : name;
    })
    .join(', ');
}

export function expoSummaryLine(order: ExpoOrder): string {
  const parts = order.tasks.map(taskSummaryLine).filter(Boolean);
  return [...new Set(parts)].join(' · ');
}
